import { defineStore } from 'pinia';
import { Light, LightApi } from '@/types/Light';
import { LightMapper } from '@/utils/mappers/LightMapper';
import { CameraSettings, CameraSettingsApi } from '@/types/CameraSettings';
import { CameraSettingsMapper } from '@/utils/mappers/CameraSettingsMapper';
import { defaultCameraSettings } from '@/data/defaultCameraSettings';
import { useSiteStore } from './site';
import { useCanvasStore } from './canvas';
import { Camera, Raycaster, Scene, WebGLRenderer } from 'three';

interface Context {
    camera?: Camera;
    renderer?: WebGLRenderer;
    scene?: Scene;
    raycaster?: Raycaster;
    [key: string]: any;
}

export const useSceneStore = defineStore('scene', {
    state: () => ({
        render2D: true as boolean,
        lights: [] as Light[],
        cameraSettings: CameraSettingsMapper.getCameraSettingsFromData(defaultCameraSettings) as CameraSettings,
        context: {} as Context
    }),
    actions: {
        setLights(lightData: LightApi[]) {
            this.lights = [];
            if (lightData.length === 0) {
                this.lights = defaultLightList.map((light) => LightMapper.getLightFromData(light.object));
                console.warn('No lights found in the scene, using default lights');
                return;
            }
            this.lights = lightData.map((light) => LightMapper.getLightFromData(light));
        },
        setCameraSettings(cameraSettingsData: CameraSettingsApi) {
            if (!cameraSettingsData || Object.keys(cameraSettingsData).length === 0) {
                this.cameraSettings = CameraSettingsMapper.getCameraSettingsFromData(defaultCameraSettings);
                console.warn('No camera settings provided, using default camera settings');
                return;
            }
            this.cameraSettings = CameraSettingsMapper.getCameraSettingsFromData(cameraSettingsData);
        },
        setContext(camera: Camera, renderer: WebGLRenderer, scene: Scene, raycaster: Raycaster) {
            this.context.camera = camera;
            this.context.renderer = renderer;
            this.context.scene = scene;
            this.context.raycaster = raycaster;
        },
        renderAll() {
            this.render2D = true;
            const canvasStore = useCanvasStore();
            if (
                this.context &&
                this.context.camera &&
                this.context.renderer &&
                this.context.scene &&
                canvasStore.isCanvasTextureMounted
            ) {
                this.context.renderer.render(this.context.scene, this.context.camera);
            }
        }
    },
    getters: {
        backgroundColor: () => {
            const siteStore = useSiteStore();
            return siteStore.site.sceneBackgroundColor || undefined;
        }
    }
});
